import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useStoreCart } from "@/context/StoreCartContext";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ArrowLeft, ShoppingBag, ChevronDown, Check } from "lucide-react";
import { API } from "@/lib/api";
const authImg = (u) => (u ? (u.startsWith("http") ? u : `${API}${u.replace(/^\/api/, "")}?auth=${localStorage.getItem("pns_token")}`) : null);

const SIDE_LABEL = { "4_0": "One side", "4_4": "Both sides" };
const ADDON_LABEL = {
  lamination: ["Lamination", "Stronger, premium finish"],
  hot_foil: ["Hot Foil", "Shiny gold/metallic accents"],
  round_corners: ["Round Corners", "Soft rounded edges"],
};

export default function StoreProduct() {
  const { id } = useParams();
  const nav = useNavigate();
  const cart = useStoreCart();

  const [product, setProduct] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTech, setShowTech] = useState(false);
  const [videos, setVideos] = useState([]);
  const [spin, setSpin] = useState([]);
  const [spinIdx, setSpinIdx] = useState(0);
  const [drag, setDrag] = useState(null);
  const [autoSpin, setAutoSpin] = useState(true);

  const [qty, setQty] = useState(100);
  const [sides, setSides] = useState("4_0");
  const [addons, setAddons] = useState({ lamination: false, hot_foil: false, round_corners: false });
  const [paperId, setPaperId] = useState(null);
  const [turnaround, setTurnaround] = useState(null);
  const [needsSetup, setNeedsSetup] = useState(false);

  const fetchPrice = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/store/paper-price", {
        product_id: id, quantity: qty, sides, turnaround_id: turnaround,
        laminate: !!addons.lamination, hot_foil: !!addons.hot_foil, round_corners: !!addons.round_corners,
      });
      setProduct(data.product);
      setCfg(data.config);
      setOptions(data.options || []);
      if (turnaround == null && data.config?.default_turnaround) setTurnaround(data.config.default_turnaround);
      setPaperId((prev) => {
        if (prev && (data.options || []).some((o) => o.paper_id === prev)) return prev;
        const def = (data.options || []).find((o) => o.is_default) || (data.options || [])[0];
        return def ? def.paper_id : null;
      });
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  }, [id, qty, sides, addons, turnaround]);

  useEffect(() => { fetchPrice(); }, [fetchPrice]);
  useEffect(() => { api.get(`/store/products/${id}/videos`).then(({ data }) => setVideos(data)).catch(() => {}); }, [id]);
  useEffect(() => { api.get(`/store/products/${id}/spin`).then(({ data }) => setSpin(data.frames || [])).catch(() => {}); }, [id]);
  useEffect(() => { if (spin.length < 2 || !autoSpin) return; const t = setInterval(() => setSpinIdx((i) => (i + 1) % spin.length), 700); return () => clearInterval(t); }, [spin, autoSpin]);

  useEffect(() => {
    if (product?.name) {
      document.title = product.seo_title || `${product.name} · Print and Save`;
      let m = document.querySelector('meta[name="description"]');
      if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
      if (product.seo_description) m.content = product.seo_description;
    }
  }, [product]);

  const selected = options.find((o) => o.paper_id === paperId);
  const allowedAddons = cfg?.addons || {};
  const showAddon = (k) => allowedAddons[k];

  const addToCart = () => {
    if (!selected) return;
    const turnLabel = (cfg?.turnarounds || []).find((t) => t.id === turnaround)?.label;
    const fh = cfg?.file_handling || {};
    const fee = needsSetup ? Number(fh.fee || 0) : 0;
    const gstP = cfg?.gst_pct || 0, pstP = cfg?.pst_pct || 0, isWs = cfg?.role === "reseller";
    const feeGst = +(fee * gstP / 100).toFixed(2);
    const feePst = isWs ? 0 : +(fee * pstP / 100).toFixed(2);
    const feeIncl = +(fee + feeGst + feePst).toFixed(2);
    const lineKey = `${id}|${paperId}|${qty}|${sides}|${addons.lamination ? 1 : 0}${addons.hot_foil ? 1 : 0}${addons.round_corners ? 1 : 0}|${turnaround}|${needsSetup ? 1 : 0}`;
    const extras = [addons.lamination && "Lam", addons.hot_foil && "Foil", addons.round_corners && "Round"].filter(Boolean).join(", ");
    const label = `${product?.name} · ${selected.paper_name} · ${qty} pcs · ${SIDE_LABEL[sides]}${extras ? ` · ${extras}` : ""}${turnLabel ? ` · ${turnLabel}` : ""}${fee ? " · File setup" : ""}`;
    cart.add({
      lineKey, product_id: id, name: label,
      unitPrice: +(selected.price + fee).toFixed(2), priceInclTax: +(selected.price_incl_tax + feeIncl).toFixed(2),
      gst: +(selected.gst + feeGst).toFixed(2), pst: +(selected.pst + feePst).toFixed(2), qty: 1,
      config: { quantity: qty, sides, paper_id: paperId, turnaround_id: turnaround, needs_setup: needsSetup, laminate: !!addons.lamination, hot_foil: !!addons.hot_foil, round_corners: !!addons.round_corners },
    });
    toast.success("Added to cart");
    nav("/store");
  };

  const quantities = cfg?.quantities || [25, 50, 100, 250, 500, 1000, 2500, 5000];
  const sideOpts = cfg?.sides || ["4_0", "4_4"];

  return (
    <div data-testid="store-product-page" className="max-w-5xl mx-auto p-6 pb-28">
      <style>{`@keyframes kenburns{0%{transform:scale(1)}100%{transform:scale(1.08)}}.kenburns{animation:kenburns 6s ease-in-out infinite alternate}`}</style>
      <button onClick={() => nav("/store")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4" data-testid="store-back">
        <ArrowLeft size={16} /> Back to store
      </button>

      <div className="grid md:grid-cols-2 gap-8">
        <div>
          {spin.length > 1 ? (
            <div className="w-full rounded-2xl overflow-hidden aspect-square bg-slate-50 relative select-none cursor-ew-resize touch-none" data-testid="sp-spin"
                 onPointerDown={(e) => { e.currentTarget.setPointerCapture?.(e.pointerId); setAutoSpin(false); setDrag({ x: e.clientX, idx: spinIdx }); }}
                 onPointerMove={(e) => { if (!drag) return; const step = e.currentTarget.getBoundingClientRect().width / spin.length; const d = Math.round((e.clientX - drag.x) / Math.max(1, step)); setSpinIdx((((drag.idx + d) % spin.length) + spin.length) % spin.length); }}
                 onPointerUp={() => setDrag(null)} onPointerLeave={() => setDrag(null)}>
              <img src={authImg(spin[spinIdx])} alt={product?.name} className="w-full h-full object-cover pointer-events-none" draggable={false} />
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[10px] bg-black/50 text-white px-2 py-0.5 rounded-full">360° · drag to rotate</div>
            </div>
          ) : product?.image_url
            ? <div className="w-full rounded-2xl overflow-hidden aspect-square"><img src={authImg(product.image_url)} alt={product?.name} className="w-full h-full object-cover kenburns" /></div>
            : <div className="w-full rounded-2xl bg-slate-100 aspect-square flex items-center justify-center text-slate-300"><ShoppingBag size={64} /></div>}
        </div>

        <div className="space-y-7">
          <div>
            <h1 className="font-head font-black text-3xl">{product?.name || "…"}</h1>
            {product?.description && <p className="text-slate-500 mt-1">{product.description}</p>}
          </div>

          {/* Quantity */}
          <div>
            <div className="text-sm font-semibold mb-2">How many?</div>
            <div className="flex flex-wrap gap-2" data-testid="sp-quantities">
              {quantities.map((q) => (
                <button key={q} onClick={() => setQty(q)} data-testid={`sp-qty-${q}`}
                  className={`num rounded-full px-5 py-2 text-sm border transition-colors ${qty === q ? "bg-[#2495D3] text-white border-[#2495D3]" : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"}`}>{q}</button>
              ))}
            </div>
          </div>

          {/* Sides */}
          {sideOpts.length > 1 && (
            <div>
              <div className="text-sm font-semibold mb-2">Printing</div>
              <div className="grid grid-cols-2 gap-3" data-testid="sp-sides">
                {sideOpts.map((sv) => (
                  <button key={sv} onClick={() => setSides(sv)} data-testid={`sp-side-${sv}`}
                    className={`rounded-xl border px-4 py-3 text-left transition-colors ${sides === sv ? "border-[#2495D3] ring-1 ring-[#2495D3] bg-blue-50/40" : "border-slate-200 hover:border-slate-300"}`}>
                    <div className="font-semibold text-sm">{SIDE_LABEL[sv]}</div>
                    <div className="text-[11px] text-slate-400">{sv === "4_0" ? "Front only" : "Front & back"}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Add-ons */}
          {["lamination", "hot_foil", "round_corners"].some(showAddon) && (
            <div>
              <div className="text-sm font-semibold mb-2">Extras</div>
              <div className="space-y-2" data-testid="sp-addons">
                {["lamination", "hot_foil", "round_corners"].filter(showAddon).map((k) => (
                  <div key={k} className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3">
                    <div>
                      <div className="text-sm font-medium">{ADDON_LABEL[k][0]}</div>
                      <div className="text-[11px] text-slate-400">{ADDON_LABEL[k][1]}</div>
                    </div>
                    <Switch data-testid={`sp-addon-${k}`} checked={!!addons[k]} onCheckedChange={(v) => setAddons((a) => ({ ...a, [k]: v }))} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Turnaround */}
          {(cfg?.turnarounds?.length > 0) && (
            <div>
              <div className="text-sm font-semibold mb-2">Turnaround</div>
              {cfg.turnarounds.length > 1 ? (
                <div className="flex flex-wrap gap-2" data-testid="sp-turnarounds">
                  {cfg.turnarounds.map((t) => (
                    <button key={t.id} onClick={() => setTurnaround(t.id)} data-testid={`sp-turn-${t.id}`}
                      className={`rounded-full px-4 py-2 text-sm border transition-colors ${turnaround === t.id ? "bg-[#2495D3] text-white border-[#2495D3]" : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"}`}>{t.label}</button>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-slate-200 px-4 py-3 text-sm font-medium" data-testid="sp-turn-fixed">{cfg.turnarounds[0].label}</div>
              )}
            </div>
          )}

          {/* Paper choice with live prices */}
          <div>
            <div className="text-sm font-semibold mb-2">Choose your paper</div>
            <div className="grid sm:grid-cols-2 gap-3" data-testid="sp-papers">
              {options.map((o, idx) => {
                const isSel = o.paper_id === paperId;
                return (
                  <button key={o.paper_id} onClick={() => setPaperId(o.paper_id)} data-testid="sp-paper-option"
                    className={`text-left rounded-xl border p-4 transition-all relative ${isSel ? "border-[#2495D3] ring-2 ring-[#2495D3]" : "border-slate-200 hover:border-slate-300"}`}>
                    {isSel && <span className="absolute top-3 right-3 text-[#2495D3]"><Check size={16} /></span>}
                    <div className="flex items-center gap-2">
                      <div className="font-head font-bold text-sm">{o.paper_name}</div>
                      {o.is_default && <span className="text-[9px] font-mono uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Popular</span>}
                      {idx === 0 && !o.is_default && <span className="text-[9px] font-mono uppercase bg-emerald-500 text-white px-1.5 py-0.5 rounded">Best price</span>}
                    </div>
                    <div className="num text-2xl font-black text-[#2495D3] mt-2">{money(o.price_incl_tax)}</div>
                    <div className="text-[11px] text-slate-400">incl. tax · {money((o.price_incl_tax || 0) / (qty || 1))}/unit</div>
                  </button>
                );
              })}
              {options.length === 0 && !loading && <div className="text-sm text-slate-400 col-span-2 py-6 text-center">No papers available for these options.</div>}
            </div>
          </div>

          {/* Your artwork / file setup */}
          {cfg?.file_handling?.fee > 0 && (
            <div data-testid="sp-file-setup">
              <div className="text-sm font-semibold mb-2">Your artwork</div>
              <div className="space-y-2">
                <button onClick={() => setNeedsSetup(false)} data-testid="sp-file-ready"
                  className={`w-full text-left rounded-xl border px-4 py-3 transition-colors ${!needsSetup ? "border-[#2495D3] ring-1 ring-[#2495D3] bg-blue-50/40" : "border-slate-200"}`}>
                  <div className="text-sm font-medium">I'll upload a print-ready PDF (free)</div>
                  <div className="text-[11px] text-slate-400">Flattened, high-res PDF using our template (correct bleed &amp; resolution).</div>
                </button>
                <button onClick={() => setNeedsSetup(true)} data-testid="sp-file-setup-needed"
                  className={`w-full text-left rounded-xl border px-4 py-3 transition-colors ${needsSetup ? "border-amber-400 ring-1 ring-amber-400 bg-amber-50/60" : "border-slate-200"}`}>
                  <div className="text-sm font-medium">I need you to set up my file (+{money(cfg.file_handling.fee)})</div>
                  <div className="text-[11px] text-slate-500">For TIFF / non-print-ready files we prep it for production. Fee added to your order.</div>
                </button>
              </div>
            </div>
          )}

          {/* Tax breakdown for the selected paper */}
          {selected && (
            <div className="rounded-xl border border-slate-200 p-4" data-testid="sp-tax-breakdown">
              <div className="flex justify-between text-sm py-0.5"><span className="text-slate-500">Subtotal</span><span className="num">{money(selected.subtotal)}</span></div>
              <div className="flex justify-between text-sm py-0.5"><span className="text-slate-500">GST {cfg?.gst_pct}%</span><span className="num">{money(selected.gst)}</span></div>
              <div className="flex justify-between text-sm py-0.5"><span className="text-slate-500">PST {cfg?.pst_pct}%</span><span className="num">{cfg?.role === "reseller" ? "—" : money(selected.pst)}</span></div>
              <div className="flex justify-between text-base font-bold pt-1.5 border-t border-slate-100 mt-1"><span>Total incl. tax</span><span className="num text-[#2495D3]">{money(selected.price_incl_tax)}</span></div>
            </div>
          )}

          {/* Technical details (collapsible) */}
          <div>
            <button onClick={() => setShowTech((s) => !s)} className="flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-slate-400 hover:text-slate-600" data-testid="sp-tech-toggle">
              Technical details <ChevronDown size={14} className={`transition-transform ${showTech ? "rotate-180" : ""}`} />
            </button>
            {showTech && selected && (
              <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 space-y-1" data-testid="sp-tech-block">
                <div>Paper: <b>{selected.paper_name}</b></div>
                <div>Quantity: <b>{qty}</b> pcs · {SIDE_LABEL[sides]}</div>
                <div>Imposition: <b>{selected.n_up}</b>-up · <b>{selected.sheets}</b> sheets</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* How-to videos shared with customers */}
      {videos.length > 0 && (
        <div className="mt-12" data-testid="sp-videos">
          <h2 className="font-head font-black text-xl mb-4">How-to &amp; guides</h2>
          <div className="grid gap-6 md:grid-cols-2">
            {videos.map((v) => (
              <div key={v.id} className="rounded-xl overflow-hidden border border-slate-200 bg-white" data-testid="sp-video">
                <div className="aspect-video bg-slate-900">
                  {v.embed_url
                    ? <iframe src={v.embed_url} title={v.title_en || v.title_es || "video"} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                    : <a href={v.url} target="_blank" rel="noreferrer" className="w-full h-full flex items-center justify-center text-white/70 text-sm">Open video</a>}
                </div>
                {(v.title_es || v.title_en) && <div className="p-3 text-sm font-semibold">{v.title_es || v.title_en}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* You may also like */}
      {(product?.related?.length > 0) && (
        <div className="mt-12" data-testid="sp-related">
          <h2 className="font-head font-black text-xl mb-4">You may also like</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {product.related.map((r) => (
              <button key={r.id} data-testid="sp-related-card" onClick={() => (r.configurable ? nav(`/store/product/${r.id}`) : nav("/store"))}
                className="text-left bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow">
                {r.image_url
                  ? <img src={r.image_url} alt={r.name} className="w-full h-28 object-cover" />
                  : <div className="w-full h-28 bg-slate-100 flex items-center justify-center text-slate-300"><ShoppingBag size={26} /></div>}
                <div className="p-3">
                  <div className="text-sm font-semibold line-clamp-1">{r.name}</div>
                  <div className="text-[11px] text-slate-400">{r.configurable ? "Configure options" : money(r.price)}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sticky add bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-slate-200 px-6 py-3 flex items-center justify-between z-20" data-testid="sp-sticky-bar">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-slate-400">Total incl. tax</div>
          <div className="num text-2xl font-black text-slate-900" data-testid="sp-total">{selected ? money(selected.price_incl_tax) : "—"}</div>
        </div>
        <Button onClick={addToCart} disabled={!selected || loading} className="bg-[#2495D3] hover:bg-[#1E7AA9] rounded-xl h-12 px-8 text-base" data-testid="sp-add-to-cart">
          <ShoppingBag size={18} className="mr-2" /> Add to cart
        </Button>
      </div>
    </div>
  );
}
